import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Field, FieldGroup } from './Field';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  ENVIRONMENT_HELP,
  ENVIRONMENT_SECRET_HELP,
  isSavedSecret,
  newEnvironmentRow,
  type EnvironmentChange,
  type EnvironmentErrors,
  type EnvironmentField,
  type EnvironmentRow,
} from '../lib/environmentForm';

interface EnvironmentVariablesFieldProps {
  rows: EnvironmentRow[];
  /** `change` says what moved, so the parent clears exactly the errors it made stale. */
  onChange: (rows: EnvironmentRow[], change: EnvironmentChange) => void;
  errors: EnvironmentErrors;
  disabled?: boolean;
  /**
   * Variables the next version reads from a Kubernetes Secret. They are not
   * editable here, and the API carries them over whatever is submitted, so the
   * field names them instead of offering an edit that would not happen.
   */
  kubernetesSecretNames?: string[];
}

/**
 * The environment variables a version hands its container, as the publish wizard
 * edits them. The rules and the help copy live in `lib/environmentForm.ts`; the
 * read-only view is `EnvironmentVariablesList`.
 */
export const EnvironmentVariablesField: React.FC<EnvironmentVariablesFieldProps> = ({
  rows,
  onChange,
  errors,
  disabled = false,
  kubernetesSecretNames = [],
}) => {
  // Ticking Secret changes which value rules apply, so it clears the value's error.
  const update = (rowId: string, field: EnvironmentField, patch: Partial<EnvironmentRow>) =>
    onChange(
      rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)),
      { rowId, field },
    );

  return (
    <FieldGroup
      legend="Environment variables"
      description={ENVIRONMENT_HELP}
      help={ENVIRONMENT_SECRET_HELP}
      error={errors.list}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...rows, newEnvironmentRow()], { list: true })}
        >
          <Plus aria-hidden="true" />
          Add variable
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No environment variables.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row, index) => {
            const rowErrors = errors.rows[row.rowId] ?? {};
            // Named by what was typed, so a screen reader hears which variable
            // each Name and Value belongs to.
            const title = row.name || `Variable ${index + 1}`;
            const kept = isSavedSecret(row) && row.secret;
            return (
              <li key={row.rowId} className="rounded-md border p-3">
                <FieldGroup legend={title} legendHidden>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Name" required error={rowErrors.name}>
                      {(control) => (
                        <Input
                          {...control}
                          mono
                          value={row.name}
                          placeholder="S3_BUCKET"
                          disabled={disabled}
                          onChange={(event) => update(row.rowId, 'name', { name: event.target.value })}
                        />
                      )}
                    </Field>
                    <Field
                      label="Value"
                      error={rowErrors.value}
                      help={kept ? 'The saved value is kept. Type a new one to replace it.' : undefined}
                    >
                      {(control) => (
                        <Input
                          {...control}
                          mono
                          // A secret is masked while typed, like every other secret on screen.
                          type={row.secret ? 'password' : 'text'}
                          autoComplete="off"
                          value={row.value}
                          placeholder={kept ? 'Saved value hidden' : undefined}
                          disabled={disabled}
                          onChange={(event) => update(row.rowId, 'value', { value: event.target.value })}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`${row.rowId}-secret`}
                        checked={row.secret}
                        disabled={disabled}
                        onCheckedChange={(checked) => update(row.rowId, 'value', { secret: checked === true })}
                      />
                      <Label htmlFor={`${row.rowId}-secret`} className="cursor-pointer font-normal">
                        Secret
                      </Label>
                    </div>
                    <Button
                      type="button"
                      variant="ghost-destructive"
                      size="icon-sm"
                      aria-label={`Remove ${title}`}
                      disabled={disabled}
                      onClick={() => onChange(rows.filter((item) => item.rowId !== row.rowId), { list: true })}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </FieldGroup>
              </li>
            );
          })}
        </ul>
      )}

      {kubernetesSecretNames.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          Also read from a Kubernetes Secret when the container starts, and kept as they are:{' '}
          <span className="font-mono">{kubernetesSecretNames.join(', ')}</span>.
        </p>
      ) : null}
    </FieldGroup>
  );
};
